import { createContext, useContext, useState, useCallback, useRef } from 'react';

/**
 * The channel between a page and the floating AgentChatDock (mounted once in
 * DefaultLayout). Three things travel over it:
 *
 * 1. `openDockWithAgent(key)` — open the dock pre-set to an agent.
 * 2. `registerScreenContext(fn)` — a page tells the dock what it is currently showing, so
 *    the agent can act on "add a step here" without the user pasting anything. Registered
 *    as a GETTER, not a value: the workflow builder's context changes on every canvas edit,
 *    and re-rendering the dock (and its whole message list) on each keystroke just to keep
 *    a prop fresh would be wasteful. The dock calls it once, at send time.
 * 3. `proposal` — the reverse direction. A tool result the dock picked out of a finished
 *    turn's traces and that the page knows how to apply (Workflow Architect proposing a
 *    graph for the canvas).
 */
const ChatDockContext = createContext(null);

export function ChatDockProvider({ children }) {
  const [openRequest, setOpenRequest] = useState(null);
  const [proposal, setProposal] = useState(null);
  // A ref, not state: registering must never trigger a re-render of the dock.
  const screenContextRef = useRef(null);

  // Functional update, so the nonce is read from the live value rather than a stale
  // closure — the old form depended on `openRequest` and re-created itself every render.
  const openDockWithAgent = useCallback(
    (agentKey) => setOpenRequest((prev) => ({ agentKey, at: prev ? prev.at + 1 : 1 })),
    [],
  );

  /** @returns {() => void} unregister — call it on unmount, so a page that has navigated
   *  away can never keep answering on behalf of the page that replaced it. */
  const registerScreenContext = useCallback((fn) => {
    screenContextRef.current = fn;
    return () => { if (screenContextRef.current === fn) screenContextRef.current = null; };
  }, []);

  const getScreenContext = useCallback(() => {
    try {
      return screenContextRef.current ? screenContextRef.current() : null;
    } catch {
      // A context builder throwing must never take send() down with it — a turn without
      // context is degraded, a turn that never sends is broken.
      return null;
    }
  }, []);

  const publishProposal = useCallback((next) => setProposal(next), []);
  const clearProposal = useCallback(() => setProposal(null), []);

  return (
    <ChatDockContext.Provider
      value={{
        openRequest, openDockWithAgent,
        registerScreenContext, getScreenContext,
        proposal, publishProposal, clearProposal,
      }}
    >
      {children}
    </ChatDockContext.Provider>
  );
}

export function useChatDock() {
  return useContext(ChatDockContext);
}
